import React, { useState, useCallback, useEffect } from 'react';

import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';

const VideoTutorial = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API endpoint for fetching tutorial videos only
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/video-tutorials`;

  // Fetch tutorial videos from database
  const fetchTutorialVideos = async () => {
    try {
      setLoading(true);
      // Changed to fetch only tutorial videos
      const response = await fetch(`${API_BASE_URL}/public/tutorials`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched tutorial videos:', data);
      
      // Filter to ensure only tutorial videos (extra safety)
      const tutorialVideos = data.filter(video => 
        video.videoType === 'TUTORIAL' || video.videoType === 'tutorial'
      );
      
      setVideos(tutorialVideos);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutorial videos:', err);
      setError('Failed to load tutorial videos');
      
      // Fallback to static tutorial data if API fails
      setVideos([
        {
          id: 1,
          title: "One Idiot - An Analysis",
          embedId: "dQw4w9WgXcQ",
          description: "Learn about investment strategies and analysis",
          videoType: "TUTORIAL"
        },
        {
          id: 2,
          title: "Why Systematic Investment Plan Works",
          embedId: "dQw4w9WgXcQ",
          description: "Understanding the power of systematic investing",
          videoType: "TUTORIAL"
        },
        {
          id: 3,
          title: "It's time to try new things",
          embedId: "dQw4w9WgXcQ",
          description: "Exploring new investment opportunities",
          videoType: "TUTORIAL"
        },
        {
          id: 4,
          title: "Options in Mutual Funds",
          embedId: "dQw4w9WgXcQ",
          description: "Different mutual fund options explained",
          videoType: "TUTORIAL"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch videos on component mount
  useEffect(() => {
    fetchTutorialVideos();
  }, []);

  // Memoized video rendering for better performance - only video iframe
  const renderVideo = useCallback((video) => (
    <div key={video.id} className="video-card">
      <div className="video-wrapper">
        <iframe
          src={`https://www.youtube.com/embed/${video.embedId}`}
          title={video.title}
          allowFullScreen
          loading="lazy"
          className="video-iframe"
        />
      </div>
    </div>
  ), []);

  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa'
    },
    videoHeader: {
      background: '#1a2332',
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px, 30px 30px',
      padding: '2rem 0',
      marginBottom: '0',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    videoHeaderContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem',
      position: 'relative',
      zIndex: 2
    },
    videoTitle: {
      fontSize: '2rem',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0',
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    },
    videoSubtitle: {
      fontSize: '1.125rem',
      color: 'rgba(255,255,255,0.8)',
      margin: '0.5rem 0 0 0',
      textAlign: 'center'
    },
    section: {
      padding: '2rem 0',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      flex: 1
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    videoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
      width: '100%'
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '300px',
      fontSize: '1.125rem',
      color: '#6b7280'
    },
    error: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '2rem',
      textAlign: 'center',
      border: '1px solid #fca5a5'
    },
    noVideos: {
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '1.125rem',
      padding: '3rem 0'
    },
    illustrationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '3rem',
      padding: '0 2rem'
    },
    illustrationLeft: {
      width: '250px',
      height: '180px'
    },
    illustrationRight: {
      width: '250px',
      height: '180px'
    }
  };

  return (
    <>
      <style>{`
        /* Base video card styling - only video */
        .video-card {
          position: relative;
          width: 100%;
          height: 0;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          border-radius: 12px;
          overflow: hidden;
          background: #000;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          will-change: transform;
        }

        .video-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 12px;
          overflow: hidden;
        }

        .video-iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 12px;
          object-fit: cover;
        }

        .video-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
        }

        /* Performance optimizations */
        * {
          box-sizing: border-box;
        }

        /* Prevent layout shifts */
        .video-grid {
          contain: layout;
        }

        /* iOS specific optimizations */
        @supports (-webkit-overflow-scrolling: touch) {
          .video-card {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
        }

        /* Comprehensive device support */
        
        /* iPhone SE (320px) */
        @media (max-width: 320px) {
          .responsive-container {
            padding: 0 0.5rem !important;
          }
          .video-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
          }
          .video-title {
            font-size: 1.1rem !important;
          }
        }

        /* iPhone 6/7/8 (375px) */
        @media (min-width: 321px) and (max-width: 375px) {
          .responsive-container {
            padding: 0 0.75rem !important;
          }
          .video-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .video-title {
            font-size: 1.2rem !important;
          }
        }

        /* iPhone 6/7/8 Plus, iPhone X/11/12 (414px) */
        @media (min-width: 376px) and (max-width: 414px) {
          .responsive-container {
            padding: 0 1rem !important;
          }
          .video-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .video-title {
            font-size: 1.3rem !important;
          }
        }

        /* iPhone 12 Pro Max, iPhone 13 Pro Max (428px) */
        @media (min-width: 415px) and (max-width: 428px) {
          .responsive-container {
            padding: 0 1rem !important;
          }
          .video-grid {
            grid-template-columns: 1fr !important;
            gap: 1.2rem !important;
          }
          .video-title {
            font-size: 1.4rem !important;
          }
        }

        /* Small tablets and large phones (429px - 640px) */
        @media (min-width: 429px) and (max-width: 640px) {
          .responsive-container {
            padding: 0 1.5rem !important;
          }
          .video-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          .video-title {
            font-size: 1.5rem !important;
          }
        }

        /* iPad Mini, small tablets (641px - 768px) */
        @media (min-width: 641px) and (max-width: 768px) {
          .responsive-container {
            padding: 0 1.5rem !important;
          }
          .video-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
            gap: 1.5rem !important;
          }
          .video-title {
            font-size: 1.6rem !important;
          }
        }

        /* iPad (769px - 1024px) */
        @media (min-width: 769px) and (max-width: 1024px) {
          .responsive-container {
            padding: 0 2rem !important;
          }
          .video-grid {
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)) !important;
            gap: 2rem !important;
          }
          .video-title {
            font-size: 1.8rem !important;
          }
        }

        /* iPad Pro (1025px - 1366px) */
        @media (min-width: 1025px) and (max-width: 1366px) {
          .responsive-container {
            padding: 0 2rem !important;
          }
          .video-grid {
            grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)) !important;
            gap: 2rem !important;
          }
          .video-title {
            font-size: 2rem !important;
          }
        }

        /* Desktop (1367px+) */
        @media (min-width: 1367px) {
          .video-grid {
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)) !important;
            gap: 2.5rem !important;
          }
        }

        /* Android specific optimizations */
        @media (max-width: 480px) {
          .video-card {
            border-radius: 8px; /* Slightly smaller radius for small screens */
          }
          .video-wrapper {
            border-radius: 8px;
          }
          .video-iframe {
            border-radius: 8px;
          }
        }

        /* Landscape orientation optimizations */
        @media (max-height: 500px) and (orientation: landscape) {
          .video-header {
            padding: 1rem 0 !important;
          }
          .video-title {
            font-size: 1.2rem !important;
          }
          .section {
            padding: 1rem 0 !important;
          }
        }

        /* High DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .video-card {
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }
          .video-card:hover {
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
          }
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .video-card {
            transition: none !important;
          }
          .video-card:hover {
            transform: none !important;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .video-card {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }
          .video-card:hover {
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
          }
        }

        /* Illustrations responsive */
        @media (max-width: 768px) {
          .illustration-container {
            margin-top: 2rem !important;
            padding: 0 1rem !important;
          }
          .illustration-left, .illustration-right {
            width: 150px !important;
            height: 120px !important;
          }
        }

        @media (max-width: 640px) {
          .illustration-container {
            flex-direction: column !important;
            gap: 1rem !important;
            align-items: center !important;
          }
          .illustration-left, .illustration-right {
            width: 120px !important;
            height: 100px !important;
          }
        }

        @media (max-width: 480px) {
          .illustration-left, .illustration-right {
            width: 100px !important;
            height: 80px !important;
          }
        }

        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden;
        }

        /* Ensure smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Optimize for touch devices */
        @media (hover: none) and (pointer: coarse) {
          .video-card:hover {
            transform: none;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }
        }
      `}</style>
      
      <div style={styles.pageContainer}>
        <Header />
        
        <section style={styles.videoHeader}>
          <div style={styles.videoHeaderContent} className="responsive-container">
            <h1 style={styles.videoTitle} className="video-title">
              Video Tutorials
            </h1>
            <p style={styles.videoSubtitle}>
              Learn about investing, financial planning, and wealth building
            </p>
          </div>
        </section>
        
        <main>
          <section style={styles.section}>
            <div style={styles.container} className="responsive-container">
              {error && (
                <div style={styles.error}>
                  {error}. Showing fallback tutorial content.
                </div>
              )}

              {loading ? (
                <div style={styles.loading}>
                  <div>Loading video tutorials...</div>
                </div>
              ) : videos.length === 0 ? (
                <div style={styles.noVideos}>
                  No video tutorials available at the moment. Check back soon!
                </div>
              ) : (
                <div style={styles.videoGrid} className="video-grid">
                  {videos.map(renderVideo)}
                </div>
              )}

              <div style={styles.illustrationContainer} className="illustration-container">
                <div style={styles.illustrationLeft} className="illustration-left">
                  <svg width="250" height="180" viewBox="0 0 250 180" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="blueJacket" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#0ea5e9" />
                        <stop offset="100%" stopColor="#0284c7" />
                      </linearGradient>
                    </defs>
                    
                    <circle cx="80" cy="35" r="15" fill="#fbbf24" />
                    <path d="M 74 28 Q 80 22 86 28 Q 82 25 80 25 Q 78 25 74 28" fill="#92400e" />
                    
                    <path d="M 55 55 Q 62 48 80 52 Q 98 48 105 55 L 108 90 Q 105 98 95 98 L 65 98 Q 55 98 52 90 Z" fill="url(#blueJacket)" />
                    
                    <path d="M 70 60 Q 80 56 90 60 L 92 90 L 68 90 Z" fill="#ffffff" />
                    
                    <ellipse cx="40" cy="70" rx="12" ry="25" fill="url(#blueJacket)" transform="rotate(-25 40 70)" />
                    <ellipse cx="120" cy="65" rx="10" ry="20" fill="url(#blueJacket)" transform="rotate(35 120 65)" />
                    
                    <circle cx="28" cy="85" r="6" fill="#fbbf24" />
                    <circle cx="130" cy="55" r="6" fill="#fbbf24" />
                    
                    <path d="M 65 98 L 95 98 L 98 140 L 88 140 L 85 108 L 75 108 L 72 140 L 62 140 Z" fill="#1e293b" />
                    
                    <ellipse cx="67" cy="148" rx="10" ry="8" fill="#2563eb" />
                    <ellipse cx="93" cy="148" rx="10" ry="8" fill="#2563eb" />
                    
                    <rect x="70" y="110" width="25" height="18" rx="2" fill="#374151" />
                    <rect x="72" y="112" width="21" height="14" rx="1" fill="#e5e7eb" />
                  </svg>
                </div>

                <div style={styles.illustrationRight} className="illustration-right">
                  <svg width="250" height="180" viewBox="0 0 250 180" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="cyanBg" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#22d3ee" />
                        <stop offset="100%" stopColor="#0891b2" />
                      </linearGradient>
                      <linearGradient id="orangeShirt" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f97316" />
                        <stop offset="100%" stopColor="#ea580c" />
                      </linearGradient>
                    </defs>
                    
                    <path d="M 150 15 Q 230 40 220 110 Q 210 160 160 155 Q 120 150 125 105 Q 130 60 150 15 Z" fill="url(#cyanBg)" />
                    
                    <circle cx="180" cy="40" r="15" fill="#fbbf24" />
                    <path d="M 174 33 Q 180 27 186 33 Q 182 30 180 30 Q 178 30 174 33" fill="#92400e" />
                    
                    <path d="M 160 60 Q 168 52 180 56 Q 192 52 200 60 L 203 95 Q 200 105 190 105 L 170 105 Q 160 105 157 95 Z" fill="url(#orangeShirt)" />
                    
                    <ellipse cx="145" cy="75" rx="8" ry="18" fill="url(#orangeShirt)" transform="rotate(-25 145 75)" />
                    <ellipse cx="215" cy="75" rx="8" ry="18" fill="url(#orangeShirt)" transform="rotate(25 215 75)" />
                    
                    <circle cx="138" cy="90" r="5" fill="#fbbf24" />
                    <circle cx="222" cy="90" r="5" fill="#fbbf24" />
                    
                    <path d="M 170 105 L 190 105 L 192 145 L 183 145 L 181 115 L 179 115 L 177 145 L 168 145 Z" fill="#1e293b" />
                    
                    <ellipse cx="173" cy="153" rx="8" ry="6" fill="#2563eb" />
                    <ellipse cx="187" cy="153" rx="8" ry="6" fill="#2563eb" />
                    
                    <rect x="168" y="115" width="22" height="15" rx="1" fill="#374151" />
                    <rect x="170" y="117" width="18" height="11" rx="1" fill="#f3f4f6" />
                  </svg>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default VideoTutorial;